import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Stethoscope, FileText, ChevronRight, Phone, ClipboardList,
  Heart, Plus, X, Check, Loader2, ChevronDown, Send, Sparkles, Edit3,
  MessageSquare
} from 'lucide-react';
import { api } from '../../services/api';
import { toast } from 'sonner';
import { ChartCommunicationThread, ORIGIN_CONFIG, PRIORITY_CONFIG } from '../ChartCommunicationThread';
import { NewCommunicationForm } from '../NewCommunicationForm';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusDot({ status }) {
  const isSigned = status === 'finalized' || status === 'signed';
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${isSigned ? 'bg-emerald-500' : 'bg-amber-400'}`} />
        </TooltipTrigger>
        <TooltipContent side="right"><p>{isSigned ? 'Signed' : 'Draft'}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const NOTE_TYPE_CONFIG = {
  telephone: { label: 'Telephone', icon: Phone, color: 'text-blue-500', bg: 'bg-blue-50' },
  log:       { label: 'Log Note', icon: ClipboardList, color: 'text-slate-500', bg: 'bg-slate-50' },
  nursing:   { label: 'Nursing', icon: Heart, color: 'text-rose-500', bg: 'bg-rose-50' },
};

export default function NotesContent({ encounters, navigate, patientId }) {
  const [noteTab, setNoteTab] = useState('soap');
  const [clinicalNotes, setClinicalNotes] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [loadingComms, setLoadingComms] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showNewComm, setShowNewComm] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [originFilter, setOriginFilter] = useState('all');
  const [expandedNote, setExpandedNote] = useState(null);
  const [expandedComm, setExpandedComm] = useState(null); // open thread id
  const [startingNote, setStartingNote] = useState(null);

  const handleStartSOAPNote = async (mode) => {
    setStartingNote(mode);
    try {
      const res = await api.post('/encounters/start-from-chart', {
        patient_id: patientId, visit_type: 'Office Visit', note_mode: mode,
      });
      toast.success(mode === 'manual' ? 'Manual note started' : 'Visit opened — generate note from transcript');
      navigate(`/visit/${res.data.encounter.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start note');
    } finally { setStartingNote(null); }
  };

  // Load clinical notes
  const fetchNotes = useCallback(() => {
    if (!patientId) return;
    setLoadingNotes(true);
    api.get(`/patients/${patientId}/clinical-notes`).then(r => setClinicalNotes(r.data.notes || [])).catch(() => {}).finally(() => setLoadingNotes(false));
  }, [patientId]);

  // Load all communications (conversations) for patient
  const fetchComms = useCallback(() => {
    if (!patientId) return;
    setLoadingComms(true);
    api.get(`/patients/${patientId}/communications`).then(r => setCommunications(r.data.communications || [])).catch(() => {}).finally(() => setLoadingComms(false));
  }, [patientId]);

  useEffect(() => { if (noteTab === 'clinical') fetchNotes(); }, [noteTab, fetchNotes]);
  useEffect(() => { if (noteTab === 'communications') fetchComms(); }, [noteTab, fetchComms]);

  const filteredNotes = filterType === 'all' ? clinicalNotes : clinicalNotes.filter(n => n.note_type === filterType);
  const filteredComms = originFilter === 'all' ? communications : communications.filter(c => (c.origin_tag || 'team') === originFilter);

  return (
    <div className="space-y-4" data-testid="notes-content">
      {/* Tab toggle: SOAP Notes | Clinical Notes | Communications */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit flex-wrap">
        <button className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${noteTab === 'soap' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
          onClick={() => setNoteTab('soap')} data-testid="notes-tab-soap">
          <Stethoscope className="w-3 h-3 inline mr-1" /> SOAP Notes
        </button>
        <button className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${noteTab === 'clinical' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
          onClick={() => setNoteTab('clinical')} data-testid="notes-tab-clinical">
          <FileText className="w-3 h-3 inline mr-1" /> Clinical Notes
          {clinicalNotes.length > 0 && <Badge variant="outline" className="text-[8px] ml-1 h-3.5">{clinicalNotes.length}</Badge>}
        </button>
        <button className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${noteTab === 'communications' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
          onClick={() => setNoteTab('communications')} data-testid="notes-tab-communications">
          <MessageSquare className="w-3 h-3 inline mr-1" /> Communications
          {communications.length > 0 && <Badge variant="outline" className="text-[8px] ml-1 h-3.5">{communications.length}</Badge>}
        </button>
      </div>

      {noteTab === 'soap' ? (
        /* ── SOAP Notes (existing encounter-based) ── */
        <div className="space-y-3" data-testid="soap-notes-list">
          {/* Start SOAP Note button — always visible in SOAP tab */}
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">
              {encounters.length} visit note{encounters.length !== 1 ? 's' : ''}
            </span>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs border-violet-200 text-violet-700 hover:bg-violet-50"
                onClick={() => handleStartSOAPNote('manual')}
                disabled={!!startingNote}
                data-testid="start-manual-note-btn">
                {startingNote === 'manual' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Edit3 className="w-3 h-3" />}
                Manual Note
              </Button>
              <Button size="sm" className="h-7 gap-1 text-xs"
                onClick={() => handleStartSOAPNote('ai')}
                disabled={!!startingNote}
                data-testid="start-soap-note-btn">
                {startingNote === 'ai' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Start SOAP Note
              </Button>
            </div>
          </div>

          {encounters.length === 0 ? (
            <div className="text-center py-10">
              <Stethoscope className="w-8 h-8 mx-auto mb-2 text-slate-200" />
              <p className="text-sm text-slate-400">No visit notes yet</p>
              <p className="text-xs text-slate-300 mt-1">Use "Start SOAP Note" to begin a visit</p>
            </div>
          ) : (
            encounters.map((enc, i) => {
              const summary = enc.encounter_outputs?.visit_summary?.summary_short;
              return (
                <Card key={enc.id || i}
                  className="cursor-pointer hover:shadow-md hover:border-slate-300 transition-all group"
                  onClick={() => navigate(enc.status === 'finalized' ? `/note/${enc.id}` : `/visit/${enc.id}`)}
                  data-testid={`soap-note-card-${i}`}>
                  <CardContent className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <StatusDot status={enc.status} />
                      <span className="text-[11px] text-slate-400 tabular-nums font-mono">{formatDate(enc.date)}</span>
                      {enc.provider_name && <span className="text-xs text-slate-500">{enc.provider_name}</span>}
                      {enc.source === 'chart_direct' && (
                        <Badge variant="outline" className="text-[8px] px-1 bg-violet-50 text-violet-600 border-violet-200">Chart</Badge>
                      )}
                      <ChevronRight className="w-3.5 h-3.5 text-slate-200 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    {summary ? (
                      <p className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-2">{summary}</p>
                    ) : (
                      <p className="text-[11px] text-slate-300 mt-1 italic">No summary available</p>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      ) : (
        /* ── Clinical Notes (telephone, log, nursing) ── */
        <div className="space-y-3" data-testid="clinical-notes-section">
          {/* Header: filter icons + create button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button onClick={() => setFilterType('all')}
                className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${filterType === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-700'}`}>
                All
              </button>
              {Object.entries(NOTE_TYPE_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const count = clinicalNotes.filter(n => n.note_type === key).length;
                return (
                  <button key={key} onClick={() => setFilterType(key)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${filterType === key ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-700'}`}
                    data-testid={`filter-${key}`}>
                    <Icon className="w-3 h-3" />
                    {cfg.label}
                    {count > 0 && <span className="text-[8px] opacity-70">({count})</span>}
                  </button>
                );
              })}
            </div>
            <Button size="sm" className="h-7 gap-1 text-xs rounded-full" onClick={() => setShowCreate(!showCreate)}
              data-testid="create-note-btn">
              {showCreate ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {showCreate ? 'Close' : 'New Note'}
            </Button>
          </div>

          {/* Create note form */}
          {showCreate && (
            <CreateNoteForm patientId={patientId} onCreated={() => { setShowCreate(false); fetchNotes(); }} onClose={() => setShowCreate(false)} />
          )}

          {/* Notes list */}
          {loadingNotes ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
          ) : filteredNotes.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-8 h-8 mx-auto mb-2 text-slate-200" />
              <p className="text-sm text-slate-400">{filterType === 'all' ? 'No clinical notes yet' : `No ${NOTE_TYPE_CONFIG[filterType]?.label || ''} notes`}</p>
              <p className="text-xs text-slate-300 mt-1">Click "New Note" to create one</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredNotes.map(note => {
                const cfg = NOTE_TYPE_CONFIG[note.note_type] || NOTE_TYPE_CONFIG.log;
                const Icon = cfg.icon;
                const isExpanded = expandedNote === note.id;

                return (
                  <div key={note.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-sm transition-all"
                    data-testid={`clinical-note-${note.id}`}>
                    <button onClick={() => setExpandedNote(isExpanded ? null : note.id)}
                      className="w-full text-left px-4 py-3 flex items-center gap-3 group">
                      <div className={`p-1.5 rounded-lg ${cfg.bg}`}>
                        <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-700 truncate">{note.title}</span>
                          <StatusDot status={note.status} />
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-slate-400">{formatDate(note.created_at)}</span>
                          <span className="text-[10px] text-slate-400">{note.created_by_name}</span>
                        </div>
                      </div>
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-300 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-3 border-t border-slate-100">
                        <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap mt-2">{note.body}</p>
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-100">
                          <span className="text-[9px] text-slate-400">Signed by {note.created_by_name} · {formatDate(note.created_at)}</span>
                          <AddendumButton noteId={note.id} patientId={patientId} onCreated={fetchNotes} />
                        </div>
                        {/* Addendums */}
                        {(note.addendums || []).length > 0 && (
                          <div className="mt-2 space-y-1">
                            {note.addendums.map(add => (
                              <div key={add.id} className="pl-4 border-l-2 border-slate-200 py-1">
                                <p className="text-[10px] text-slate-500 font-medium">Addendum — {add.created_by_name} · {formatDate(add.created_at)}</p>
                                <p className="text-xs text-slate-600 mt-0.5">{add.body}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Communications Tab ── */}
      {noteTab === 'communications' && (
        <div className="space-y-3" data-testid="communications-tab">
          {/* Header row */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1 flex-wrap flex-1">
              <button onClick={() => setOriginFilter('all')}
                className={`text-[10px] px-2.5 py-1 rounded-full font-medium border transition-colors ${originFilter === 'all' ? 'bg-foreground text-background border-foreground' : 'bg-card text-muted-foreground hover:text-foreground border-border'}`}>
                All
              </button>
              {Object.entries(ORIGIN_CONFIG).map(([k, cfg]) => (
                <button key={k} onClick={() => setOriginFilter(k)}
                  className={`text-[10px] px-2.5 py-1 rounded-full font-medium border transition-colors ${originFilter === k ? 'bg-foreground text-background border-foreground' : 'bg-card text-muted-foreground hover:text-foreground border-border'}`}>
                  {cfg.label}
                </button>
              ))}
            </div>
            <Button size="sm" variant={showNewComm ? 'default' : 'outline'} className="h-7 text-xs gap-1 shrink-0"
              onClick={() => setShowNewComm(!showNewComm)} data-testid="new-comm-btn">
              {showNewComm ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {showNewComm ? 'Close' : 'New Communication'}
            </Button>
          </div>

          {showNewComm && (
            <NewCommunicationForm
              patientId={patientId}
              patientName=""
              onCreated={() => { setShowNewComm(false); fetchComms(); }}
              onClose={() => setShowNewComm(false)}
            />
          )}

          {loadingComms ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : filteredComms.length === 0 ? (
            <div className="text-center py-10">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-200" />
              <p className="text-sm text-slate-400">No communications yet</p>
              <p className="text-xs text-slate-300 mt-1">Phone notes, team messages, and nursing logs appear here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredComms.map(comm => {
                const origin = ORIGIN_CONFIG[comm.origin_tag || 'team'] || ORIGIN_CONFIG.team;
                const priority = PRIORITY_CONFIG[comm.priority || 'routine'] || PRIORITY_CONFIG.routine;
                const OriginIcon = origin.icon;
                const isExpanded = expandedComm === comm.id;
                const isResolved = comm.status === 'resolved';

                return (
                  <div key={comm.id}
                    className={`rounded-xl border ${isResolved ? 'border-emerald-200 bg-emerald-50/10' : 'bg-card border-border'} transition-all`}
                    data-testid={`comm-card-${comm.id}`}>
                    <button
                      className="w-full text-left px-4 py-3 flex items-center gap-3"
                      onClick={() => setExpandedComm(isExpanded ? null : comm.id)}>
                      <div className={`p-1.5 rounded-lg border shrink-0 ${origin.color}`}>
                        <OriginIcon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-semibold text-slate-700 truncate">{comm.title}</span>
                          <span className={`inline-flex items-center gap-0.5 text-[8px] font-semibold px-1.5 py-0.5 rounded border ${priority.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${priority.dot}`} />
                            {priority.label}
                          </span>
                          {isResolved && <Badge className="text-[8px] bg-emerald-50 text-emerald-600 border-emerald-200">Resolved</Badge>}
                          {!isResolved && comm.status === 'active' && (
                            <Badge className="text-[8px] bg-amber-50 text-amber-600 border-amber-200">Open</Badge>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-0.5 truncate">
                          {comm.message_count} message{comm.message_count !== 1 ? 's' : ''} · {comm.created_by_name || comm.created_by_role || 'Team'} · {new Date(comm.created_at).toLocaleDateString('en-US', {month:'short', day:'numeric'})}
                        </p>
                      </div>
                      <ChevronRight className={`w-4 h-4 text-slate-300 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>

                    {isExpanded && (
                      <div className="border-t" style={{ height: '400px' }}>
                        <ChartCommunicationThread
                          key={comm.id}
                          conversation={comm}
                          onUpdated={fetchComms}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* Create Note Form */
function CreateNoteForm({ patientId, onCreated, onClose }) {
  const [noteType, setNoteType] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const TYPES = [
    { key: 'telephone', label: 'Telephone Note', icon: Phone, hint: 'Phone call with patient, pharmacy, specialist', subjectHint: 'e.g., "Patient called re: lab results"' },
    { key: 'log', label: 'Log Note', icon: ClipboardList, hint: 'General clinical entry or observation', subjectHint: 'e.g., "Prior auth approved for MRI"' },
    { key: 'nursing', label: 'Nursing Note', icon: Heart, hint: 'Nursing assessment or medication admin', subjectHint: 'e.g., "B12 injection administered"' },
  ];

  const activeType = TYPES.find(t => t.key === noteType);

  const handleSubmit = async () => {
    if (!noteType || !subject || !body) return;
    setSubmitting(true);
    try {
      await api.post(`/patients/${patientId}/clinical-notes`, { note_type: noteType, subject, body });
      toast.success(`${activeType?.label || 'Note'} created`);
      onCreated();
    } catch { toast.error('Failed to create note'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-white p-4 space-y-3 animate-in fade-in-0 slide-in-from-top-1 duration-150" data-testid="create-note-form">
      {!noteType ? (
        <div>
          <p className="text-xs text-slate-500 mb-2">What type of note?</p>
          <div className="grid grid-cols-3 gap-2">
            {TYPES.map(t => (
              <button key={t.key} onClick={() => setNoteType(t.key)}
                className="text-left p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-all"
                data-testid={`note-type-${t.key}`}>
                <t.icon className="w-5 h-5 text-slate-400 mb-1" />
                <span className="text-[11px] font-semibold text-slate-700 block">{t.label}</span>
                <span className="text-[9px] text-slate-400">{t.hint}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <activeType.icon className="w-4 h-4 text-slate-400" />
            <span className="text-xs font-semibold text-slate-700">{activeType.label}</span>
            <button onClick={() => setNoteType('')} className="text-[9px] text-blue-500">change</button>
          </div>
          <input type="text" placeholder={activeType?.subjectHint || 'Subject'} value={subject}
            onChange={e => setSubject(e.target.value)}
            className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2" autoFocus data-testid="note-subject" />
          <Textarea placeholder="Note content..." value={body} onChange={e => setBody(e.target.value)}
            className="min-h-[80px] text-sm resize-none rounded-xl border-slate-200" data-testid="note-body" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={!subject || !body || submitting}
              className="gap-1" data-testid="note-save-btn">
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Sign & Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


/* Addendum Button — inline on each note */
function AddendumButton({ noteId, patientId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/patients/${patientId}/clinical-notes`, {
        note_type: 'addendum', subject: 'Addendum', body, parent_note_id: noteId,
      });
      toast.success('Addendum added');
      setOpen(false); setBody(''); onCreated();
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-[9px] text-blue-500 hover:text-blue-700 ml-auto" data-testid={`add-addendum-${noteId}`}>
        + Addendum
      </button>
    );
  }

  return (
    <div className="flex-1 ml-auto max-w-sm space-y-1.5 animate-in fade-in-0 duration-100">
      <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Addendum text..."
        className="min-h-[40px] text-xs resize-none rounded-lg border-slate-200" autoFocus />
      <div className="flex justify-end gap-1">
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-6 text-[9px]">Cancel</Button>
        <Button size="sm" onClick={handleSubmit} disabled={!body.trim() || submitting} className="h-6 text-[9px] gap-0.5">
          {submitting ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Send className="w-2.5 h-2.5" />} Add
        </Button>
      </div>
    </div>
  );
}
